// Companion/blocks/StudySetQuestionPager.tsx
//
// The question side of a study set: one question at a time (ORA-685).
//
// A three-question set used to arrive as the first question plus "Question 2"
// chips that appended the next one *underneath* it, so a set the student
// worked through grew into a wall they had to scroll past to reach the footer.
// A set is a sequence, so it is drawn as one: a single question area, a pager
// under it, and — up in the block's own header row — a segment bar that says
// where the student is and how the ones behind them went.
//
// Nothing unmounts. Every question stays rendered and the inactive ones are
// hidden, which is what keeps an answered question answered when the student
// steps back to it. Hidden means out of layout, not transparent: `hidden` for
// the eye, the mouse and the block's height (the area is as tall as the
// question being read, so an unanswered question does not sit over the empty
// space an answered neighbour's explanation would take — ORA-696), `inert` and
// `aria-hidden` for the keyboard and the screen reader, and a no-op
// `onAnchorChange` so a question off screen can never be what the companion
// is anchored to.
//
// The page number lives in `StudySetBlock`, because the header that shows it is
// the block's. This component owns the stepping, not the position.

import type { HTMLAttributes, KeyboardEvent } from 'react';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useScrollEdgeFade, verticalEdgeFadeClass } from '../useScrollEdgeFade';
import type { ChatStudySetItem } from '@/types/chatBlocks';
import type { ChatAnchorInput } from '@/types/chatEnvelope';
import { QuestionBlockBody } from './bodies/QuestionBlockBody/QuestionBlockBody';
import { toLegacyItem, toSeedAnchor } from './QuestionBlock';
import type { QuestionStudyBadges } from './useQuestionStudyBadges';

/** One question of a set, as the set's items carry it. */
export type StudySetQuestionItem = Extract<
  ChatStudySetItem,
  { kind: 'question' }
>;

/**
 * `inert` as React 18 can write it. The attribute is not in React 18's DOM
 * property list — React 19 types it — and the empty string is how the DOM
 * reads it as present.
 *
 * Exported because the block hides a folded set the same way it hides an
 * inactive question (ORA-833): mounted, out of layout, out of reach.
 */
export const INERT_PROPS = {
  inert: '',
} as unknown as HTMLAttributes<HTMLDivElement>;

/**
 * What a segment says about its question, and the fill that says it.
 *
 * `answered` is a question that arrived done: the server keeps the count but
 * not the correctness, so the segment is filled and neutral rather than
 * guessing green. The fill is `fg-secondary` at 40% — an opacity on a token, so
 * it stays right in both themes — because `border-subtle` as a fill is the same
 * value as the unanswered segment's outline and the two would not read apart.
 */
const SEGMENT_TONES = {
  correct: { className: 'bg-status-success', label: 'correct' },
  wrong: { className: 'bg-status-danger', label: 'wrong' },
  answered: { className: 'bg-fg-secondary/40', label: 'answered' },
  unanswered: {
    className: 'border border-border-subtle bg-surface-muted',
    label: 'unanswered',
  },
} as const;

type SegmentTone = (typeof SEGMENT_TONES)[keyof typeof SEGMENT_TONES];

const segmentTone = (
  item: StudySetQuestionItem,
  answers: Map<number, boolean>
): SegmentTone => {
  const answered = answers.get(item.ord);

  if (answered === undefined) {
    return item.done ? SEGMENT_TONES.answered : SEGMENT_TONES.unanswered;
  }

  return answered ? SEGMENT_TONES.correct : SEGMENT_TONES.wrong;
};

export interface StudySetQuestionProgressProps {
  items: StudySetQuestionItem[];
  answers: Map<number, boolean>;
  activeIndex: number;
}

/**
 * The set's progress for the block header: a segment per question and the page
 * number beside it.
 *
 * It reads rather than acts. The arrow buttons and the Left/Right keys are the
 * navigation, and a row of 6px targets pretending to be a second one is neither
 * reachable on a phone nor worth a tab stop — so this is one `img` with a
 * sentence, which is also all a screen reader needs to hear.
 */
export const StudySetQuestionProgress = ({
  items,
  answers,
  activeIndex,
}: StudySetQuestionProgressProps) => {
  const tones = items.map((item) => segmentTone(item, answers));

  const counts = tones.reduce<Record<string, number>>((totals, tone) => {
    totals[tone.label] = (totals[tone.label] ?? 0) + 1;
    return totals;
  }, {});

  const summary = (['correct', 'wrong', 'answered', 'unanswered'] as const)
    .filter((label) => counts[label])
    .map((label) => `${counts[label]} ${label}`)
    .join(', ');

  const countText = `Question ${activeIndex + 1} of ${items.length}`;

  return (
    <>
      <div
        className="flex items-center gap-1"
        role="img"
        aria-label={`${countText}: ${summary}`}
        data-testid="study-set-question-progress"
      >
        {tones.map((tone, index) => (
          <span
            key={items[index].ord}
            className={cn(
              'block h-1.5 rounded-full',
              // Three is the inline ceiling for questions today; a longer set
              // keeps the bar the same width by making its segments narrower.
              items.length > 3 ? 'w-6' : 'w-8',
              tone.className,
              // The ring, not the fill, is what marks the position: an
              // unanswered active segment has no colour of its own. The offset
              // is the block's own surface, not the page's, since that is what
              // the segment sits on.
              index === activeIndex &&
                'ring-2 ring-ring ring-offset-1 ring-offset-surface-raised'
            )}
          />
        ))}
      </div>
      {/* Below `mobile:` the header has no room for it and the pager row
          carries it instead. */}
      <span className="hidden text-xs text-fg-secondary mobile:inline">
        {countText}
      </span>
    </>
  );
};

export interface StudySetQuestionPagerProps {
  /** Rides on every answer, so answering here marks the set's item done. */
  setId?: string;
  /**
   * The block the questions belong to, when they are a course QBANK step's
   * (Courses V1) rather than a set's: rides on every answer instead.
   */
  blockId?: string;
  /** The set's questions, in the order the server put them in. */
  items: StudySetQuestionItem[];
  /**
   * The answers given in this block, by item ord: true where the first try was
   * right. Owned by the block, which also counts them into the footer.
   */
  answers: Map<number, boolean>;
  /** Which question is on screen. Owned by the block; its header shows it. */
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  /** Claim the chat anchor. Only ever called for the question in view. */
  claim: (anchor: ChatAnchorInput) => void;
  onBeforeContentExpand?: () => void;
  onAnswerConfirmed: (
    item: StudySetQuestionItem,
    firstAttemptCorrect: boolean
  ) => void;
  /**
   * The badges a missed question offers (ORA-831), from the block's own hook.
   * Only the question on screen gets them: every question stays mounted, and a
   * badge row nobody can see is a linked-content read for nothing.
   */
  badges: QuestionStudyBadges;
  /**
   * How the pager takes height. `fixed` (the chat block) is as tall as the
   * question being read. `fill` (a course step, Courses V1) takes the height
   * its host offers: the question scrolls inside its area and Previous / Next
   * stay at the bottom, as the flashcard deck does.
   */
  layout?: 'fixed' | 'fill';
}

const StudySetQuestionPager = ({
  layout = 'fixed',
  setId,
  blockId,
  items,
  answers,
  activeIndex,
  onActiveIndexChange,
  claim,
  onBeforeContentExpand,
  onAnswerConfirmed,
  badges,
}: StudySetQuestionPagerProps) => {
  const isRevealed = (item: StudySetQuestionItem) =>
    answers.has(item.ord) || Boolean(item.question.selectedLetter);

  const goTo = (index: number) => {
    if (index < 0 || index >= items.length || index === activeIndex) return;

    onActiveIndexChange(index);

    const item = items[index];

    // Claimed from the student's step, never from a render effect: that is
    // `useBlockAnchor`'s rule, and it is what keeps "the last block engaged
    // wins" true when several blocks of one turn are all on screen.
    claim({
      type: 'question',
      vignetteId: item.question.vignetteId,
      variantId: item.question.variantId ?? null,
      revealed: isRevealed(item),
    } satisfies ChatAnchorInput);
  };

  // Arrows step the pager only while the focus is inside it. Nothing is bound
  // globally: the thread scrolls with the arrow keys, and a block that ate them
  // would take that away from every other block on the page.
  const onPagerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    goTo(activeIndex + (event.key === 'ArrowLeft' ? -1 : 1));
  };

  // A one-question set is a served question. A pager under it would only say so
  // twice; the header leaves its aside off for the same reason.
  const hasPager = items.length > 1;

  // In the fill layout the question scrolls inside its area and fades at
  // whichever edge has more; the fixed layout's area is as tall as its
  // question and never scrolls, so it goes unwatched.
  const {
    ref: areaRef,
    hasMoreAbove,
    hasMoreBelow,
  } = useScrollEdgeFade<HTMLDivElement>();

  return (
    <div
      className={cn(
        'flex flex-col gap-3',
        layout === 'fill' && 'min-h-0 flex-1'
      )}
      data-testid="study-set-question-pager"
      data-active-ord={items[activeIndex]?.ord}
      data-layout={layout}
    >
      {/* Every question stays mounted (its answer and explanation live in it),
          but only the active one takes part in layout: an inactive question is
          `hidden`, so the area is exactly as tall as the question being read
          — an unanswered stem and its options, or an answered one with its
          explanation — and does not keep the height of the tallest one in the
          set (ORA-696). */}
      <div
        ref={layout === 'fill' ? areaRef : undefined}
        className={cn(
          'min-w-0',
          // A course step has the room: the stem and options (sized in em)
          // read a step larger than in the chat block.
          layout === 'fill' &&
            'min-h-0 flex-1 overflow-y-auto text-base md:text-lg',
          layout === 'fill' && verticalEdgeFadeClass(hasMoreAbove, hasMoreBelow)
        )}
        data-testid="study-set-question-area"
      >
        {items.map((item, index) => {
          const isActive = index === activeIndex;

          return (
            <div
              key={item.ord}
              aria-hidden={isActive ? undefined : true}
              className={cn('min-w-0', !isActive && 'hidden')}
              data-testid="study-set-item"
              data-item-ord={item.ord}
              {...(isActive ? {} : INERT_PROPS)}
            >
              <QuestionBlockBody
                item={toLegacyItem(item.question)}
                anchor={toSeedAnchor(item.question)}
                studySetId={setId}
                blockId={blockId}
                onBeforeContentExpand={onBeforeContentExpand}
                onAnswerConfirmed={(firstAttemptCorrect) =>
                  onAnswerConfirmed(item, firstAttemptCorrect)
                }
                {...badges.badgePropsFor({
                  vignetteId: item.question.vignetteId,
                  isVisible: isActive,
                })}
                onAnchorChange={
                  isActive
                    ? (anchor) => {
                        const vignette = (anchor?.vignette ?? {}) as Record<
                          string,
                          unknown
                        >;

                        claim({
                          type: 'question',
                          vignetteId: item.question.vignetteId,
                          variantId: item.question.variantId ?? null,
                          revealed: vignette.revealed === true,
                        } satisfies ChatAnchorInput);
                      }
                    : () => {}
                }
              />
            </div>
          );
        })}
      </div>

      {hasPager ? (
        <div
          className={cn(
            'flex items-center justify-between gap-2',
            layout === 'fill' && 'shrink-0'
          )}
          onKeyDown={onPagerKeyDown}
        >
          <div className="flex min-w-0 items-center gap-2">
            <Button
              aria-label="Previous Question"
              className="min-h-11 min-w-11"
              disabled={activeIndex === 0}
              onClick={() => goTo(activeIndex - 1)}
              size="sm"
              type="button"
              variant="outline"
            >
              <ArrowLeft aria-hidden="true" />
              <span className="hidden mobile:inline">Previous</span>
            </Button>
            {/* Narrow widths have no room for the count in the header, so it
                sits here instead — see Kevin's 390px frame. */}
            <span className="truncate text-xs text-fg-secondary mobile:hidden">
              {`Question ${activeIndex + 1} of ${items.length}`}
            </span>
          </div>
          {/* No "Finish" on the last question: the footer's one button is the
              way out of a set, and a second finishing action beside it read as
              a rival to it. */}
          <Button
            aria-label="Next Question"
            className="min-h-11"
            disabled={activeIndex === items.length - 1}
            onClick={() => goTo(activeIndex + 1)}
            size="sm"
            type="button"
            variant="outline"
          >
            <span className="hidden mobile:inline">Next Question</span>
            <span className="mobile:hidden">Next</span>
            <ArrowRight aria-hidden="true" />
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default StudySetQuestionPager;
